<template>
  <div id="app-shell" class="min-h-screen bg-surface text-on-surface">
    <!-- ====== Stack overlay (full-screen, slides over tab content) ====== -->
    <transition :name="stackTransition">
      <div
        v-if="isStackRoute"
        id="app-stack"
        class="fixed inset-0 z-60 bg-surface overflow-y-auto overflow-x-hidden"
        style="-webkit-overflow-scrolling: touch; overscroll-behavior: contain;"
      >
        <router-view />
      </div>
    </transition>

    <!-- ====== Main tab area（v-show 保持各 Tab 挂载，滚动位置不丢失） ====== -->
    <div :class="{ 'opacity-0 pointer-events-none': isStackRoute }" class="transition-opacity duration-100">
      <keep-alive :include="tabPageNames">
        <HomePage v-if="visitedTabs.home" v-show="currentTab === 'home' && !isStackRoute" />
        <NearbyPage v-if="visitedTabs.nearby" v-show="currentTab === 'nearby' && !isStackRoute" />
        <PublishPage v-if="visitedTabs.publish" v-show="currentTab === 'publish' && !isStackRoute" />
        <WantedListPage v-if="visitedTabs['wanted-list']" v-show="currentTab === 'wanted-list' && !isStackRoute" />
        <ProfilePage v-if="visitedTabs.profile" v-show="currentTab === 'profile' && !isStackRoute" />
      </keep-alive>
    </div>

    <!-- ====== Tab bar ====== -->
    <AppTabbar
      v-if="showTabbar"
      :active-tab="activeTab"
      @switch="onTabSwitch"
    />

    <div
      class="fixed bottom-24 left-1/2 bg-on-surface text-surface px-6 py-3 rounded-full shadow-2xl z-[100] pointer-events-none"
      :class="toastVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'"
      style="transform: translateX(-50%); transition: opacity 0.3s, transform 0.3s;"
    >
      <div class="flex items-center gap-2">
        <span class="material-symbols-outlined text-[20px]">info</span>
        <span class="font-label-md text-label-md">{{ toastMessage }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import AppTabbar from '@/components/AppTabbar.vue';
import HomePage from '@/views/HomePage.vue';
import NearbyPage from '@/views/NearbyPage.vue';
import PublishPage from '@/views/PublishPage.vue';
import WantedListPage from '@/views/WantedListPage.vue';
import ProfilePage from '@/views/ProfilePage.vue';
import store from '@/store';

const TAB_NAMES = ['home', 'nearby', 'publish', 'wanted-list', 'profile'];
const TAB_COMPONENT_NAMES = ['HomePage', 'NearbyPage', 'PublishPage', 'wanted-list', 'profile'];
const PENDING_TOAST_KEY = 'pendingToast';

export default {
  name: 'AppShell',
  components: {
    AppTabbar,
    HomePage,
    NearbyPage,
    PublishPage,
    WantedListPage,
    ProfilePage
  },
  data() {
    return {
      stackTransition: 'stack-slide-left',
      lastTab: 'home',
      visitedTabs: {
        home: false,
        nearby: false,
        publish: false,
        'wanted-list': false,
        profile: false
      },
      toastVisible: false,
      toastMessage: '',
      toastTimer: null
    };
  },
  computed: {
    isStackRoute() {
      return !!this.$route.meta.stack;
    },
    activeTab() {
      return this.$route.meta.tab || this.lastTab;
    },
    currentTab() {
      return this.$route.meta.tab || this.lastTab;
    },
    showTabbar() {
      return !this.isStackRoute && !this.$route.meta.hideTabbar;
    },
    tabPageNames() {
      return TAB_COMPONENT_NAMES.join(',');
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(to, from) {
        this.consumePendingToast();
        if (to.meta.tab) {
          this.$set(this.visitedTabs, to.meta.tab, true);
          this.lastTab = to.meta.tab;
        }

        // Determine slide direction for stack transitions
        if (from && to.meta.stack && from.meta.stack) {
          this.stackTransition = 'stack-slide-left';
        } else if (from && to.meta.stack && !from.meta.stack) {
          this.stackTransition = 'stack-slide-left';
        } else if (from && !to.meta.stack && from.meta.stack) {
          this.stackTransition = 'stack-slide-right';
        }

        // Emit page lifecycle events for backward compatibility
        this.$nextTick(() => {
          if (to.meta.tab) {
            this.$root.$emit('page:activate', { page: to.meta.tab, type: 'tab' });
            document.dispatchEvent(new CustomEvent('page:activate', { detail: { page: to.meta.tab, type: 'tab' } }));
          }
          if (from && from.meta && from.meta.tab) {
            this.$root.$emit('page:deactivate', { page: from.meta.tab, type: 'tab' });
            document.dispatchEvent(new CustomEvent('page:deactivate', { detail: { page: from.meta.tab, type: 'tab' } }));
          }
        });
      }
    }
  },
  methods: {
    showToast(message) {
      this.toastMessage = message || '';
      this.toastVisible = true;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toastVisible = false;
      }, 2500);
    },

    consumePendingToast() {
      const message = sessionStorage.getItem(PENDING_TOAST_KEY);
      if (!message) return;
      sessionStorage.removeItem(PENDING_TOAST_KEY);
      this.showToast(message);
    },

    onTabSwitch(tab) {
      if (tab === this.activeTab) return;
      if (tab === 'publish' && !store.isLoggedIn) {
        this.showToast('未登录，请先登录');
        return;
      }
      if (TAB_NAMES.indexOf(tab) !== -1) {
        this.$router.push({ name: tab });
      }
    }
  },
  mounted() {
    this.lastTab = this.$route.meta.tab || 'home';
    this.consumePendingToast();
  },
  beforeDestroy() {
    clearTimeout(this.toastTimer);
  }
};
</script>

<style>
/* Stack slide transitions */
.stack-slide-left-enter-active,
.stack-slide-left-leave-active,
.stack-slide-right-enter-active,
.stack-slide-right-leave-active {
  transition: transform 0.25s ease-out;
}

.stack-slide-left-enter {
  transform: translateX(100%);
}
.stack-slide-left-leave-to {
  transform: translateX(-30%);
}

.stack-slide-right-enter {
  transform: translateX(-30%);
}
.stack-slide-right-leave-to {
  transform: translateX(100%);
}

/* Tab switching */
#app-shell .transition-opacity {
  transition: opacity 0.1s ease-out;
}

/* 各 Tab 页独立滚动容器 */
.tab-page-root {
  height: 100dvh;
  overflow: hidden;
}
.tab-page-scroll {
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}
</style>
